import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import AddRounded from '@mui/icons-material/AddRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import ImageRounded from '@mui/icons-material/ImageRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';
import PageHeader from '../components/common/PageHeader';
import ImageUploader from '../components/common/ImageUploader';
import {
  getOptionCategories, createOptionCategory, updateOptionCategory, deleteOptionCategory,
  createOption, updateOption, deleteOption,
} from '../services/optionsApi';

const Options = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [deleting, setDeleting] = useState(null);

  // Category dialog
  const [catDialog, setCatDialog] = useState({ open: false, edit: null });
  const [catName, setCatName] = useState('');
  const [savingCat, setSavingCat] = useState(false);
  const [catError, setCatError] = useState('');

  // Option dialog
  const [optDialog, setOptDialog] = useState({ open: false, categoryId: null, edit: null });
  const [optTitle, setOptTitle] = useState('');
  const [optImage, setOptImage] = useState('');
  const [savingOpt, setSavingOpt] = useState(false);
  const [optError, setOptError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const data = await getOptionCategories();
      setCategories(data.items || []);
    } catch (err) {
      console.error('Failed to fetch options:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Categories ───
  const openCreateCat = () => { setCatDialog({ open: true, edit: null }); setCatName(''); setCatError(''); };
  const openEditCat = (cat) => { setCatDialog({ open: true, edit: cat }); setCatName(cat.name); setCatError(''); };
  const handleSaveCat = async () => {
    if (!catName.trim()) { setCatError('Name is required'); return; }
    setSavingCat(true); setCatError('');
    try {
      if (catDialog.edit) await updateOptionCategory(catDialog.edit.id, { name: catName.trim() });
      else await createOptionCategory(catName.trim());
      setCatDialog({ open: false, edit: null });
      await fetchData();
    } catch (err) {
      setCatError(err.response?.data?.message || 'Failed to save');
    } finally { setSavingCat(false); }
  };
  const handleDeleteCat = async (id) => {
    if (!window.confirm('Delete this category and all its options?')) return;
    setDeleting(`cat-${id}`);
    try { await deleteOptionCategory(id); await fetchData(); }
    catch (err) { console.error('Delete failed:', err); }
    finally { setDeleting(null); }
  };

  // ─── Options ───
  const openCreateOpt = (categoryId) => {
    setOptDialog({ open: true, categoryId, edit: null });
    setOptTitle(''); setOptImage(''); setOptError('');
  };
  const openEditOpt = (categoryId, opt) => {
    setOptDialog({ open: true, categoryId, edit: opt });
    setOptTitle(opt.title); setOptImage(opt.image_url || ''); setOptError('');
  };
  const handleSaveOpt = async () => {
    if (!optTitle.trim()) { setOptError('Title is required'); return; }
    setSavingOpt(true); setOptError('');
    try {
      if (optDialog.edit) {
        await updateOption(optDialog.edit.id, { title: optTitle.trim(), image_url: optImage.trim() });
      } else {
        await createOption({ option_category_id: optDialog.categoryId, title: optTitle.trim(), image_url: optImage.trim() });
      }
      setOptDialog({ open: false, categoryId: null, edit: null });
      await fetchData();
    } catch (err) {
      setOptError(err.response?.data?.message || 'Failed to save');
    } finally { setSavingOpt(false); }
  };
  const handleDeleteOpt = async (id) => {
    if (!window.confirm('Delete this option?')) return;
    setDeleting(`opt-${id}`);
    try { await deleteOption(id); await fetchData(); }
    catch (err) { console.error('Delete failed:', err); }
    finally { setDeleting(null); }
  };

  const toggleCat = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  if (loading) {
    return (
      <Box>
        <PageHeader title="Options" subtitle="Manage option categories and their options" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Options"
        subtitle="Manage option categories and options — selectable on each product page"
        action={
          <Button variant="contained" startIcon={<AddRounded />} onClick={openCreateCat}>
            Add Category
          </Button>
        }
      />

      {categories.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <TuneRounded sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">No option categories yet</Typography>
            <Typography variant="body2" color="text.secondary">
              Click "Add Category" to create your first category (e.g. "Edge Options", "Column Options")
            </Typography>
          </Box>
        </Card>
      ) : (
        categories.map((cat) => {
          const isExpanded = expanded[cat.id];
          return (
            <Card key={cat.id} sx={{ mb: 1.5 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                onClick={() => toggleCat(cat.id)}
              >
                <IconButton size="small" sx={{ mr: 0.5 }}>
                  {isExpanded ? <ExpandMoreRounded fontSize="small" /> : <ChevronRightRounded fontSize="small" />}
                </IconButton>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>{cat.name}</Typography>
                <Chip label={`${cat.options?.length || 0} options`} size="small" sx={{ mr: 1 }} />
                <Tooltip title="Add Option">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); openCreateOpt(cat.id); }} sx={{ color: 'primary.main', mr: 0.5 }}>
                    <AddRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit Category">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditCat(cat); }} sx={{ color: 'primary.main', mr: 0.5 }}>
                    <EditRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Category">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteCat(cat.id); }} disabled={deleting === `cat-${cat.id}`} sx={{ color: 'error.main' }}>
                    {deleting === `cat-${cat.id}` ? <CircularProgress size={16} /> : <DeleteOutlineRounded fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>

              <Collapse in={isExpanded}>
                <Box sx={{ px: 4, pb: 2 }}>
                  {(!cat.options || cat.options.length === 0) ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      No options yet. Click + to add one.
                    </Typography>
                  ) : (
                    cat.options.map((opt) => (
                      <Box key={opt.id} sx={{ display: 'flex', alignItems: 'center', py: 0.75, gap: 1.5, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                        <Avatar variant="rounded" src={opt.image_url} sx={{ width: 36, height: 36, bgcolor: 'grey.100' }}>
                          <ImageRounded sx={{ fontSize: 16, color: 'grey.400' }} />
                        </Avatar>
                        <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>{opt.title}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {opt.image_url || '--'}
                        </Typography>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditOpt(cat.id, opt)} sx={{ color: 'primary.main' }}>
                            <EditRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDeleteOpt(opt.id)} disabled={deleting === `opt-${opt.id}`} sx={{ color: 'error.main' }}>
                            {deleting === `opt-${opt.id}` ? <CircularProgress size={14} /> : <DeleteOutlineRounded fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))
                  )}
                </Box>
              </Collapse>
            </Card>
          );
        })
      )}

      {/* Category Dialog */}
      <Dialog open={catDialog.open} onClose={() => setCatDialog({ open: false, edit: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{catDialog.edit ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus label="Category Name" placeholder="e.g. Edge Options" value={catName}
            onChange={(e) => { setCatName(e.target.value); setCatError(''); }}
            fullWidth sx={{ mt: 1 }} error={!!catError} helperText={catError} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCatDialog({ open: false, edit: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCat} disabled={savingCat}>
            {savingCat ? <CircularProgress size={20} color="inherit" /> : (catDialog.edit ? 'Save' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Option Dialog */}
      <Dialog open={optDialog.open} onClose={() => setOptDialog({ open: false, categoryId: null, edit: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{optDialog.edit ? 'Edit Option' : 'Add Option'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus label="Title" placeholder="e.g. Steel Round" value={optTitle}
            onChange={(e) => { setOptTitle(e.target.value); setOptError(''); }}
            fullWidth sx={{ mt: 1, mb: 2 }} error={!!optError} helperText={optError} />
          <ImageUploader value={optImage} onChange={setOptImage} folder="options" label="Option Image" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOptDialog({ open: false, categoryId: null, edit: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveOpt} disabled={savingOpt}>
            {savingOpt ? <CircularProgress size={20} color="inherit" /> : (optDialog.edit ? 'Save' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Options;
